import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Role } from "@/types/auth.types";
export const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, isLoading, user } = useAuth();
    const location = useLocation();
    if (isLoading) {
        return (_jsx("div", { style: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh",
            }, children: "Loading..." }));
    }
    if (!isAuthenticated) {
        return _jsx(Navigate, { to: "/login", state: { from: location }, replace: true });
    }
    // Role-based routing
    const path = location.pathname;
    // Admin users can only access /admin/* routes
    if (user?.role === Role.ADMIN && !path.startsWith("/admin")) {
        return _jsx(Navigate, { to: "/admin/home", replace: true });
    }
    // Staff users can only access /staff/* routes
    if (user?.role === Role.STAFF && !path.startsWith("/staff")) {
        return _jsx(Navigate, { to: "/staff/home", replace: true });
    }
    // Client users can only access /client/* routes
    if (user?.role === Role.CLIENT && !path.startsWith("/client")) {
        return _jsx(Navigate, { to: "/client/home", replace: true });
    }
    // Contractor and subcontractor users can only access /contractor/* routes
    if ((user?.role === Role.CONTRACTOR || user?.role === Role.SUBCONTRACTOR) &&
        !path.startsWith("/contractor")) {
        return _jsx(Navigate, { to: "/contractor/jobs", replace: true });
    }
    // Surveyor users can only access /surveyor/* routes
    if (user?.role === Role.SURVEYOR && !path.startsWith("/surveyor")) {
        return _jsx(Navigate, { to: "/surveyor/home", replace: true });
    }
    return _jsx(_Fragment, { children: children });
};
export const PublicRoute = ({ children }) => {
    const { isAuthenticated, isLoading, user } = useAuth();
    if (isLoading) {
        return (_jsx("div", { style: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh",
            }, children: "Loading..." }));
    }
    if (isAuthenticated && user) {
        // Redirect based on user role
        switch (user.role) {
            case Role.ADMIN:
                return _jsx(Navigate, { to: "/admin/home", replace: true });
            case Role.STAFF:
                return _jsx(Navigate, { to: "/staff/home", replace: true });
            case Role.CLIENT:
                return _jsx(Navigate, { to: "/client/home", replace: true });
            case Role.CONTRACTOR:
            case Role.SUBCONTRACTOR:
                return _jsx(Navigate, { to: "/contractor/jobs", replace: true });
            case Role.SURVEYOR:
                return _jsx(Navigate, { to: "/surveyor/home", replace: true });
            default:
                return _jsx(Navigate, { to: "/login", replace: true });
        }
    }
    return _jsx(_Fragment, { children: children });
};
export const StaffProtectedRoute = ({ children, requiredAccess, }) => {
    const { isAuthenticated, isLoading, user, hasAccess } = useAuth();
    const location = useLocation();
    if (isLoading) {
        return (_jsx("div", { style: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh",
            }, children: "Loading..." }));
    }
    if (!isAuthenticated) {
        return _jsx(Navigate, { to: "/login", state: { from: location }, replace: true });
    }
    if (!user || user.role !== Role.STAFF) {
        return _jsx(Navigate, { to: "/", replace: true });
    }
    if (requiredAccess && !hasAccess(requiredAccess)) {
        return _jsx(Navigate, { to: "/staff/home", replace: true });
    }
    return _jsx(_Fragment, { children: children });
};
